<script lang="ts">
    import {flip} from 'svelte/animate';
    import iconStore from "../store/iconStore";
    import Icon from "../parts/Icon.svelte";
    import type {Message, WritableIcon} from "../@type";
    import FileDrop, {getFilesFromEvent} from "filedrop-svelte";
    import type { Files } from "filedrop-svelte";
    import {createEventDispatcher, getContext, setContext, tick} from "svelte";
    import selectMessageStore from "../store/selectMessageStore";

    export let mode: 'normal' | 'select' = 'normal';
    export let editing = false;
    let iconListContainer;
    let hovering: number | boolean = false;
    let addFileMode = false;
    let iconList: WritableIcon[] = [];

    const dispatch = createEventDispatcher();
    iconStore.subscribe((beforeIconList: WritableIcon[]) => {
        iconList = beforeIconList;
    });

    const drop = (event, target) => {
        event.dataTransfer.dropEffect = 'move';
        const start = parseInt(event.dataTransfer.getData("text/plain"));
        moveImpl(start, target)
    }
    const move = (type: 'up' | 'down', index: number) => {
        let newIndex: number;
        if(type == 'up'){
            newIndex = Math.max(0, index - 1);
        } else {
            newIndex = Math.min(iconList.length - 1, index+1);
        }
        moveImpl(index, newIndex)
    }
    const moveImpl = (start, target) => {
        const newTracklist = iconList.concat([]);
        if (start < target) {
            newTracklist.splice(target + 1, 0, newTracklist[start]);
            newTracklist.splice(start, 1);
        } else {
            newTracklist.splice(target, 0, newTracklist[start]);
            newTracklist.splice(start + 1, 1);
        }
        iconList = newTracklist
        hovering = null
    }

    const dragstart = (event, i) => {
        event.dataTransfer.effectAllowed = 'move';
        event.dataTransfer.dropEffect = 'move';
        const start = i;
        event.dataTransfer.setData('text/plain', start);
    }

    function deleteItem(index) {
        const deletedIcon = iconList.splice(index, 1)
        const deleteUrl = deletedIcon[0].url;
        if(deleteUrl.startsWith("blob:")) {
            URL.revokeObjectURL(deleteUrl);
        }
        iconList = iconList;
    }

    async function changeImg(index) {
        if(!editing){
            return;
        }
        openFileSelectDialog().then((files) => {
            for (let fileWithPath of files.accepted) {
                iconList[index].url = URL.createObjectURL(fileWithPath);
            }
        });
    }

    function openFileSelectDialog(): Promise<Files> {
        const input = document.createElement('input');
        input.type = 'file';
        input.accept = "image/*";
        const promise = new Promise(resolve => {
            input.addEventListener('change', resolve, {once: true});
        }).then((event: Event) => {
            return getFilesFromEvent(event, {})
        });
        input.click();
        return promise;
    }

    const handleFilesSelect = async (e) => {
        const files: Files = e.detail ? e.detail.files : e;
        for (let fileWithPath of files.accepted) {
            addIcon(fileWithPath);
        }
        await tick()
        iconListContainer.parentNode.scrollTo(0, iconListContainer.parentNode.scrollHeight);
    }

    function addIcon(blob:Blob) {
        const url = URL.createObjectURL(blob);
        iconList[iconList.length] = {
            id: new Date().getMilliseconds() + Math.random(),
            order: iconList.length,
            name: "",
            url,
            speaker: "TODO",
        };
    }

    function enterEditing() {
        if(editing){
            iconStore.update(() => iconList);
        }
        addFileMode = false;
        hovering = false;
        editing = !editing;
    }

    function selectRow(index: number) {
        if(editing || mode !== 'select'){
            return;
        }
        if(hovering === index){
            iconSelect(iconList[index])
        }
        hovering = index;
    }

    const {close} = getContext("simple-modal");

    function iconSelect(icon: WritableIcon) {
        const unsubscribe = selectMessageStore.subscribe((message: Message | null) => {
            if(!message){
                throw new Error("変更対象の投稿が見つかりません");
            }
            message.icon = icon;
            close();
        });
        unsubscribe();
    }

    function enterNewIcon(e) {
        if(isMobile){
            openFileSelectDialog().then(handleFilesSelect)
            return;
        }
        addFileMode = !addFileMode;
    }
</script>
<script context="module">
    import {polyfill} from "mobile-drag-drop";
    const isMobile = polyfill();
</script>

<div class="header">
    <button type="button" on:click={enterEditing}>
        {#if editing}
            変更確定
        {:else}
            編集モード
        {/if}
    </button>
    {#if editing}
        <button type="button" class:active={addFileMode} on:click={enterNewIcon}>
            <span class="icon-images">新規追加</span>
        </button>
    {/if}
    <div class="hint">
        {#if mode === 'select' && !editing && hovering !== false}
            同じアイコンをもう一度タップすると決定
        {/if}
    </div>
</div>
{#if addFileMode}
    <FileDrop fileLimit="100" accept="image/*" on:filedrop={handleFilesSelect}>
        <div class="fileDropArea">
        {#if isMobile}
            <span>タップしてください</span>
        {:else}
            <span>タップするか、追加する画像をドラッグアンドドロップしてください。</span>
        {/if}
        </div>
    </FileDrop>
{/if}
<div class:editing={editing} bind:this={iconListContainer}>
    {#each iconList as icon, index (icon.id)}
        <div class="icon-row"
             animate:flip="{{duration: 400}}"
             draggable={editing}
             on:dragstart={event => dragstart(event, index)}
             on:drop|preventDefault={event => drop(event, index)}
             ondragover="return false"
             on:dragenter={(event) => {event.preventDefault(); hovering = index}}
             class:is-active={hovering === index}
             on:click={selectRow(index)}
        >
            <div class="index">
                {#if editing}
                    <span class="icon-menu2"/>
                {/if}
                <span>{index}</span>
            </div>
            <Icon icon={icon} name="" on:click={changeImg(index)}/>
            <div class="icon-detail">
                {#if editing}
                    <input class="name" type="text" maxlength="80" bind:value={icon.name} required>
                    <div class="edit-action">
                        <i class="icon-file-picture" on:click={changeImg(index)}><span>Change</span></i>
                        <i class="icon-move-up" on:click={move('up', index)}><span>Up</span></i>
                        <i class="icon-move-down" on:click={move('down', index)}><span>Down</span></i>
                        <i class="icon-delete" on:click={deleteItem(index)}><span>Delete</span></i>
                    </div>
                {:else}
                    <span class="name">{icon.name || "Icon #" + index}</span>
                {/if}
            </div>
            {#if mode === 'select' && !editing && hovering === index}
                <div class="select-icon"><i/></div>
            {/if}
        </div>
    {/each}
</div>

<style>
    .index {
        padding: 1em 0;
        align-items: center;
        margin: 0 0.5em;
        width: 1em;
        text-align: center;
    }
    .icon-row {
        display: flex;
        flex-direction: row;
        padding: 0.2em;
        min-height: 70px;
    }
    .icon-detail {
        display: flex;
        flex-direction: column;
        margin: 0 0.5em;
    }

    .name {
        height: 2em;
    }

    .header {
        position: sticky;
        top: 0;
        background: inherit;
        display: flex;
        flex-direction: row;
        flex-wrap: wrap;
        margin-bottom: 1rem;
        background-color: honeydew;
    }

    .is-active {
        background-color: #3273dc;
        color: #fff;
    }

    button {
        padding: 10px 20px;
        border-radius: 20px;
        background: #eee;
        display: inline-block;
        cursor: pointer;
        transition: .5s;
    }

    .select-icon {
        align-self: center;
        margin-left: auto;
        margin-right: 4px;
    }

    .hint {
        text-align: center;
        margin-left: 0.2rem;
        max-width: 55%;
    }

    @media screen and (max-width: 500px) {
        .edit-action > i > span {
            display: none;
        }
        .edit-action > i:before {
            font-size: x-large;
        }
        .edit-action > i {
            margin: 0 0.5rem;
        }
    }
</style>